/* eslint-disable quote-props */
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/naming-convention */
import { HttpBackend, HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export interface RefreshTokenResponse {
  identity_id: string;
  identity_token: string;
  refresh_token: string;
  expires_in: number;
}

export interface ResetPinResponse {
  success: boolean;
  message: string;
}

export interface UserProfileResponse {
  identity_id: string;
  email: string;
  first_name: string;
  last_name: string;
  middle_name: string;
  phone_number: string;
  gender: string;
  dob: string;
  street_address: string;
  city: string;
  state: string;
  id_type: string;
  nin: string;
  bvn: string;
  kyc_verified: boolean;
  kyc_verification_url: string;
  complete_profile_url: string;
  email_verified: boolean;
  wallet_account_number: string;
  wallet_account_name: string;
  wallet_bank_name: string;
  is_pin_set: boolean;
  is_pin_locked: boolean;
  mall_profile_id: string;
  vas_profile_id: string;
  created_at: string;
  password_update_required: boolean;
  password_policy: {
    is_compliant: boolean;
    requires_update: boolean;
    message: string;
    errors: string[];
  };
}

@Injectable({
  providedIn: 'root',
})
export class PayarenaService {
  private baseUrl = environment.baseUrl;
  private serverLogUrl = environment.serverLogUrl;
  // Refresh requests must bypass the interceptor so a rejected refresh cannot
  // recursively trigger another refresh attempt.
  private refreshHttp: HttpClient;

  constructor(
    private http: HttpClient,
    httpBackend: HttpBackend,
  ) {
    this.refreshHttp = new HttpClient(httpBackend);
  }

  refreshAccessToken(refreshToken: string) {
    const token = localStorage.getItem('token') || '';
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });
    return this.refreshHttp.post<RefreshTokenResponse>(
      // `https://test.apimall.payarena.com/api/v1/auth/refresh`,
      `https://apimall.payarena.com/api/v1/auth/refresh`,
      { refresh_token: refreshToken },
      { headers }
    );
  }

  initiatePinReset() {
    return this.http.post(`${this.baseUrl}/mobile/initiate-pin-reset`, {});
  }

  resetPin(payload: { token: string; pin: string }) {
    return this.http.post<ResetPinResponse>(`${this.baseUrl}/mobile/reset-pin`, {
      ...payload,
      authCode: 'ResetPin',
    });
  }

  getMallHeaders() {
    const token = btoa(
      `${environment.consumerKey}:${environment.consumerSecret}`
    );
    return new HttpHeaders({
      Authorization: `Basic ${token}`,
    });
  }

  getUserProfile() {
    // return this.http.get<UserProfileResponse>(`${this.baseUrl}/api/v1/auth/me`);
    return this.http.get<UserProfileResponse>(`https://apimall.payarena.com/api/v1/auth/me`);
  }

  setWalletPin(payload: { pin: string }) {
    // return this.http.post(`${this.baseUrl}/internal/identity/pin/set`, payload);
    return this.http.post(`https://apimall.payarena.com/api/v1/auth/pin/set`, payload);
  }

  // Digital Service methods
  getDashboardCatalog() {
    const services = this.http
      .get(`${this.baseUrl}/mobile/catalog`)
      .pipe(catchError((error) => of(error)));
    const verification = this.http
      .get(`${this.baseUrl}/pvs/catalog`)
      .pipe(catchError((error) => of(error)));
    return forkJoin([services, verification]);
  }

  getCatalog() {
    return this.http.get(`${this.baseUrl}/mobile/catalog`);
  }

  getNewWalletBalance() {
    return this.http.get(`${this.baseUrl}/mobile/balance`);
  }

  getNewTransactions(service?: string) {
    let params = new HttpParams();
    if (service) params = params.set('service', service);
    return this.http.get(`${this.baseUrl}/mobile/gettransactions`, { params });
  }

  validateService(body) {
    return this.http.post(`${this.baseUrl}/mobile/validate`, body);
  }

  processPayment(body) {
    return this.http.post(`${this.baseUrl}/mobile/process`, body);
  }

  // Wallet
  createWallet(body: any) {
    return this.http.post(`${this.baseUrl}/mobile/create-wallet`, body);
  }

  validatePhoneNumber() {
    return this.http.get(`${this.baseUrl}/mobile/validate-phone-number`);
  }

  getWalletBalance() {
    return this.http.get(`${this.baseUrl}/mobile/balance`);
  }

  nameEnquiry(payload: { BankCode: string; AccountNumber: string }) {
    return this.http.post(`${this.baseUrl}/transfer/name-enquiry`, payload);
  }

  getTransferCommission(amount: string) {
    return this.http.get(`${this.baseUrl}/transfer/commission?amount=${amount}`);
  }

  getBanks() {
    return this.http.get(`${this.baseUrl}/mobile/get-banks`);
  }

  transfer(payload: any) {
    return this.http.post(`${this.baseUrl}/transfer/process`, payload);
  }

  IdentityStatus() {
    return this.http.get(`${this.baseUrl}/settings/check-identity-status`)
  }

  fundWallet(payload: any) {
    return this.http.post(`${this.baseUrl}/mobile/credit-wallet`, payload);
  }

  // Verification Service methods
  getVerificationServices() {
    const verification = this.http
      .get(`${this.baseUrl}/pvs/catalog`)
      .pipe(catchError((error) => of(error)));
    const bundle = this.http
      .get(`${this.baseUrl}/pvs/bundle-catalog`)
      .pipe(catchError((error) => of(error)));
    return forkJoin([verification, bundle]);
  }

  validateVerificationService(body) {
    return this.http.post(`${this.baseUrl}/pvs/validate`, body);
  }

  validateOtpService(body) {
    return this.http.post(`${this.baseUrl}/pvs/validateauthcode`, body);
  }

  processVerificationPayment(body) {
    return this.http.post(`${this.baseUrl}/pvs/process`, body);
  }

  // Bundle Service methods
  processBundlePayment(body) {
    return this.http.post(`${this.baseUrl}/pvs/process-bundle`, body);
  }

  getTermsAndConditions() {
    return this.http.get(`${this.baseUrl}/pvs/termsandconditions`);
  }

  // Verify VAS Transaction Status
  verifyVASTransactionStatus(transactionId) {
    return this.http.get(`${this.baseUrl}/mobile/status/${transactionId}`);
  }

  // Verify PVS Transaction Status
  verifyPVSTransactionStatus(transactionId) {
    return this.http.get(`${this.baseUrl}/pvs/status/${transactionId}`);
  }

  sendErrorLogToServer(error) {
    return this.http.post(`${this.serverLogUrl}/mobile/messagelog`, error);
  }
}
